import React from "react";
import {
  ChatIcon,
  ConnectivityIcon,
  HandIcon,
  PersonIcon,
  PoorConnectivityIcon,
} from "@100mslive/react-icons";
import { Button } from "@100mslive/roomkit-react";
import {
  useIsSidepaneTypeOpen,
  useSidepaneToggle,
} from "../AppData/useSidepane";
import { SIDE_PANE_OPTIONS } from "../../common/constants";
import i18n from "../../i18n";

const ChatAction = React.forwardRef((_, ref) => {
  const toggleChat = useSidepaneToggle(SIDE_PANE_OPTIONS.CHAT);
  const isChatOpen = useIsSidepaneTypeOpen(SIDE_PANE_OPTIONS.CHAT);

  if (isChatOpen) {
    return null;
  }

  return (
    <Button
      outlined
      as="div"
      variant="standard"
      css={{ w: "max-content" }}
      onClick={toggleChat}
      ref={ref}
    >
      {i18n.t("common:open_chat")}
    </Button>
  );
});

export const ToastConfig = {
  PEER_LIST: {
    single: function (notification) {
      if (notification.data.length === 1) {
        return {
          title: i18n.t("common:x_joined", { name: notification.data[0]?.name }),
          icon: <PersonIcon />,
        };
      }
      return {
        title: i18n.t("common:x_and_y_others_joined", {
          name: notification.data[notification.data.length - 1]?.name,
          count: notification.data.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
    multiple: notifications => {
      return {
        title: i18n.t("common:x_and_y_others_joined", {
          name: notifications[0].data.name,
          count: notifications.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
  },
  PEER_JOINED: {
    single: function (notification) {
      return {
        title: i18n.t("common:x_joined", { name: notification.data?.name }),
        icon: <PersonIcon />,
      };
    },
    multiple: function (notifications) {
      return {
        title: i18n.t("common:x_and_y_others_joined", {
          name: notifications[notifications.length - 1].data.name,
          count: notifications.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
  },
  PEER_LEFT: {
    single: function (notification) {
      return {
        title: i18n.t("common:x_left", { name: notification.data?.name }),
        icon: <PersonIcon />,
      };
    },
    multiple: function (notifications) {
      return {
        title: i18n.t("common:x_and_y_others_left", {
          name: notifications[notifications.length - 1].data.name,
          count: notifications.length - 1,
        }),
        icon: <PersonIcon />,
      };
    },
  },
  METADATA_UPDATED: {
    single: notification => {
      return {
        title: i18n.t("common:x_raised_hand", { name: notification.data?.name }),
        icon: <HandIcon />,
      };
    },
    multiple: notifications => {
      return {
        title: i18n.t("common:x_and_y_others_raised_hand", {
          name: notifications[notifications.length - 1].data?.name,
          count: notifications.length - 1,
        }),
        icon: <HandIcon />,
      };
    },
  },
  NEW_MESSAGE: {
    single: notification => {
      return {
        title: i18n.t("common:new_message_from_x", { senderName: notification.data?.senderName }),
        icon: <ChatIcon />,
        action: <ChatAction />,
      };
    },
    multiple: notifications => {
      return {
        title: i18n.t("common:x_new_messages", { count: notifications.length }),
        icon: <ChatIcon />,
        action: <ChatAction />,
      };
    },
  },
  RECONNECTED: {
    single: () => {
      return {
        title: i18n.t("common:you_are_now_connected"),
        icon: <ConnectivityIcon />,
        variant: "success",
        duration: 3000,
      };
    },
  },
  RECONNECTING: {
    single: message => {
      return {
        title: i18n.t("common:you_are_offline_reconnecting"),
        icon: <PoorConnectivityIcon />,
        variant: "warning",
        duration: 30000,
      };
    },
  },
};
